import { Box, Flex, Text } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useChatMessages } from '../chat/hooks/useChatMessages';
import { SiestaAction } from '../types';
import { useAppContainer } from '../useAppContainer';
import { AIActionSet } from './AIActionSet';
import { AskSiestaInput } from './AskSiestaInput';
import { ChatEmptyState } from './ChatEmptyState';
import { CtaAdditionalAI } from './CtaAdditionalAI';
import classes from './SiestaBar.module.css';
import { SiestaBarLayout } from './SiestaBarLayout';
import { SiestaBarMessages } from './SiestaBarMessages';
import { useChatEmptyMessage } from './useChatEmptyMessage';
import { useSuggestedActions } from './useSuggestedActions';

export function SiestaBar() {
  const { siestaMode, setSiestaMode } = useAppContainer();
  const siestaActions = useSuggestedActions();
  const emptyMessage = useChatEmptyMessage();
  const { clientId } = useParams<{ clientId: string }>();
  const { messages, sendUserMessage, clearMessages } = useChatMessages();

  const handleActionClick = async (action: SiestaAction) => {
    setSiestaMode('chat');
    if (clientId) {
      await sendUserMessage(action.prompt ?? action.label, clientId);
    }
  };

  const handleInputSubmit = async (message: string) => {
    setSiestaMode('chat');
    if (clientId) {
      await sendUserMessage(message, clientId);
    }
  };

  const handleInputFocus = () => {
    setSiestaMode('chat');
  };

  const handleBackToActions = () => {
    clearMessages();
    setSiestaMode('action');
  };

  if (siestaMode === 'busy') {
    return (
      <Box className={classes.busyContainer} px="xxxl">
        <Text size="textXl" fw={FONT_WEIGHTS.semibold} c="#333" className={classes.busyText}>
          {'Siesta is busy,\nfinish the action to turn it back on'}
        </Text>
      </Box>
    );
  }

  if (siestaMode === 'chat') {
    return (
      <Flex direction="column" h="100%" pos="relative" bg="white">
        <CtaAdditionalAI onClick={handleBackToActions} />

        <Box flex={1} pt={64} style={{ overflow: 'auto' }}>
          {messages.length === 0 ? (
            <ChatEmptyState title={emptyMessage.title} subtitle={emptyMessage.subtitle} />
          ) : (
            <SiestaBarMessages messages={messages} />
          )}
        </Box>

        <Box p="md">
          <AskSiestaInput variant="brand" onSubmit={handleInputSubmit} />
        </Box>
      </Flex>
    );
  }

  return (
    <SiestaBarLayout>
      <Flex h="100%" direction="column" justify="space-between">
        <Flex flex={1} align="center" justify="center">
          <AIActionSet actions={siestaActions} onActionClick={handleActionClick} />
        </Flex>
        <AskSiestaInput variant="light" onSubmit={handleInputSubmit} onFocus={handleInputFocus} />
      </Flex>
    </SiestaBarLayout>
  );
}
